import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, RefreshCw, ShieldAlert } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Progress } from '../ui/progress';

const STEPS = [
    { label: 'Reading your call transcript', at: 0 },
    { label: 'Checking each EEOC rule', at: 6 },
    { label: 'Scoring your answers', at: 20 },
    { label: 'Writing your coaching tips', at: 38 },
];

const useElapsedSeconds = () => {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const start = Date.now();
        const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
        return () => clearInterval(timer);
    }, []);
    return elapsed;
};

const StepRow = ({ step, index, activeIndex }) => {
    const done = index < activeIndex;
    const active = index === activeIndex;
    return (
        <motion.li
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.08 }}
            className={`flex items-center gap-3 text-sm transition-colors ${done ? 'text-foreground' : active ? 'font-medium text-sky-700 dark:text-sky-300' : 'text-muted-foreground'}`}
            data-testid={`eeoc-grading-step-${index}`}
            data-state={done ? 'done' : active ? 'active' : 'pending'}
        >
            {done ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : active ? <Loader2 className="h-4 w-4 animate-spin text-sky-600" />
                : <span className="h-4 w-4 rounded-full border border-muted-foreground/40" />}
            {step.label}
        </motion.li>
    );
};

const FailedState = ({ onRetry, busy }) => (
    <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <ShieldAlert className="h-8 w-8 text-amber-500" />
        <p className="text-sm text-muted-foreground" data-testid="eeoc-grading-failed">We couldn't finish grading this call. Your conversation is saved.</p>
        <Button onClick={onRetry} disabled={busy} data-testid="eeoc-retry-grading-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />} Retry grading
        </Button>
    </CardContent>
);

export const GradingProgress = ({ failed, onRetry, busy }) => {
    const elapsed = useElapsedSeconds();
    const activeIndex = STEPS.reduce((acc, s, i) => (elapsed >= s.at ? i : acc), 0);
    const percent = Math.min(92, Math.round((elapsed / 50) * 92));

    return (
        <Card className="border-sky-200 dark:border-sky-800" data-testid="eeoc-grading-card">
            {failed ? <FailedState onRetry={onRetry} busy={busy} /> : (
                <CardContent className="space-y-5 p-6">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-sky-600">Grading your call</p>
                        <p className="mt-1 text-sm text-muted-foreground">This usually takes under a minute. You can stay on this page.</p>
                    </div>
                    <Progress value={percent} className="h-1.5" data-testid="eeoc-grading-progress" />
                    <ol className="space-y-3">
                        {STEPS.map((step, i) => <StepRow key={step.label} step={step} index={i} activeIndex={activeIndex} />)}
                    </ol>
                </CardContent>
            )}
        </Card>
    );
};
